import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import Portal from '../components/Portal';
import '../styles/Page.css';
import '../styles/Careers.css';

const hostURL = config.serverUrl;

function Careers() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Edit/Delete state
  const [editingJob, setEditingJob] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    work_model: '',
    content: '',
    compensation_min: '',
    compensation_max: '',
    location: '',
    expiration: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});

  // Fetch jobs on mount
  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await axios.get(`${hostURL}/api/jobs`);
      setJobs(response.data.jobs || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setJobs([]);
      setError('Failed to load jobs');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Required field checks
    if (!formData.title) errors.title = true;
    if (!formData.work_model) errors.work_model = true;
    if (!formData.content) errors.content = true;
    if (!formData.location) errors.location = true;
    if (!formData.expiration) errors.expiration = true;
    if (!formData.compensation_min) errors.compensation_min = true;
    if (!formData.compensation_max) errors.compensation_max = true;
    
    // Length constraints (matching database)
    if (formData.title && formData.title.length > 255) {
      setError('Title must be 255 characters or less');
      errors.title = true;
    }
    if (formData.work_model && formData.work_model.length > 20) {
      setError('Work model must be 20 characters or less');
      errors.work_model = true;
    }
    if (formData.content && formData.content.length > 10000) {
      setError('Content must be 10,000 characters or less');
      errors.content = true;
    }
    if (formData.location && formData.location.length > 255) {
      setError('Location must be 255 characters or less');
      errors.location = true;
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      if (!error) {
        setError('Please fill out all required fields');
      }
      return;
    }

    // Validate compensation range
    const minComp = parseInt(formData.compensation_min);
    const maxComp = parseInt(formData.compensation_max);
    if (minComp > maxComp) {
      setError('Minimum compensation cannot exceed maximum compensation');
      return;
    }

    try {
      await axios.post(
        `${hostURL}/api/jobs`,
        {
          ...formData,
          compensation_min: minComp,
          compensation_max: maxComp
        },
        { withCredentials: true }
      );
      
      setSuccess('Job created successfully!');
      setShowCreatePopup(false);
      setFormData({
        title: '',
        work_model: '',
        content: '',
        compensation_min: '',
        compensation_max: '',
        location: '',
        expiration: ''
      });
      setFieldErrors({});
      fetchJobs();
    } catch (err) {
      console.error('Error creating job:', err);
      setError(err.response?.data?.error || 'Failed to create job');
    }
  };

  const handleEditJob = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      work_model: job.work_model,
      content: job.content,
      compensation_min: job.compensation_min || '',
      compensation_max: job.compensation_max || '',
      location: job.location,
      expiration: job.expiration.split('T')[0] // Format date for input
    });
    setShowEditPopup(true);
    setOpenDropdownId(null);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      setError('Please fill out all required fields');
      return;
    }

    const minComp = parseInt(formData.compensation_min);
    const maxComp = parseInt(formData.compensation_max);
    if (minComp > maxComp) {
      setError('Minimum compensation cannot exceed maximum compensation');
      return;
    }

    try {
      await axios.put(
        `${hostURL}/api/jobs/${editingJob.id}`,
        {
          ...formData,
          compensation_min: minComp,
          compensation_max: maxComp
        },
        { withCredentials: true }
      );
      
      setSuccess('Job updated successfully!');
      setShowEditPopup(false);
      setEditingJob(null);
      setFormData({
        title: '',
        work_model: '',
        content: '',
        compensation_min: '',
        compensation_max: '',
        location: '',
        expiration: ''
      });
      setFieldErrors({});
      fetchJobs();
    } catch (err) {
      console.error('Error updating job:', err);
      setError(err.response?.data?.error || 'Failed to update job');
    }
  };

  const handleCancelEdit = () => {
    setShowEditPopup(false);
    setEditingJob(null);
    setFormData({
      title: '',
      work_model: '',
      content: '',
      compensation_min: '',
      compensation_max: '',
      location: '',
      expiration: ''
    });
    setFieldErrors({});
    setError('');
  };

  const handleDeleteClick = (job) => {
    setJobToDelete(job);
    setShowDeletePopup(true);
    setOpenDropdownId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(
        `${hostURL}/api/jobs/${jobToDelete.id}`,
        { withCredentials: true }
      );
      
      setSuccess('Job deleted successfully!');
      setShowDeletePopup(false);
      setJobToDelete(null);
      fetchJobs();
    } catch (err) {
      console.error('Error deleting job:', err);
      setError(err.response?.data?.error || 'Failed to delete job');
      setShowDeletePopup(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeletePopup(false);
    setJobToDelete(null);
  };

  const toggleDropdown = (jobId) => {
    setOpenDropdownId(openDropdownId === jobId ? null : jobId);
  };

  const truncateContent = (content, maxLength = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const isAdminOrOwner = currentUser && (currentUser.access === 'admin' || currentUser.access === 'owner');

  return (
    <div className="page">
      <div className="page-header">
        <h1>Careers</h1>
        <p>Join our team and make a difference</p>
      </div>

      <div className="page-content">
        <section className="content-section">
          <h2>Why Work With Us?</h2>
          <p>
            At One Aligned, we believe our people are our greatest asset. We foster a 
            culture of innovation, collaboration, and continuous growth. Join us in our 
            mission to drive sustainable success for our clients and ourselves.
          </p>
        </section>

        <section className="content-section">
          <h2>Open Positions</h2>
          
          {success && <div className="success-message">{success}</div>}
          {error && !showCreatePopup && <div className="error-message">{error}</div>}
          
          {loading ? (
            <div className="loading">Loading positions...</div>
          ) : (
            <>
              {jobs.length === 0 && !isAdminOrOwner ? (
                <div className="no-positions-message">
                  <p>There are no open positions at this time.</p>
                  <p>Please check back later for updates on new opportunities!</p>
                </div>
              ) : (
                <div className="jobs-grid">
                  {/* Add Job Card - Only visible to admins/owners */}
                  {isAdminOrOwner && (
                    <div className="add-job-card" onClick={() => setShowCreatePopup(true)}>
                      <div className="add-job-icon">+</div>
                      <p>Add New Position</p>
                    </div>
                  )}
                  
                  {/* Job Cards */}
                  {jobs.map(job => {
                    const isExpired = new Date(job.expiration) <= new Date();
                    return (
                      <div key={job.id} className={`job-card ${isExpired ? 'expired' : ''}`}>
                        <div className="job-card-header">
                          <h3>{job.title}</h3>
                          {isAdminOrOwner && (
                            <div className="job-menu">
                              <button 
                                className="menu-button"
                                onClick={() => toggleDropdown(job.id)}
                              >
                                ⋮
                              </button>
                              {openDropdownId === job.id && (
                                <div className="dropdown-menu">
                                  <button onClick={() => handleEditJob(job)}>Edit</button>
                                  <button className="delete-option" onClick={() => handleDeleteClick(job)}>Delete</button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="job-work-model">{job.work_model}</p>
                        <p className="job-expiration">
                          {isExpired ? 'Expired: ' : 'Expires: '}
                          {formatDate(job.expiration)}
                        </p>
                        <p className="job-location">Location: {job.location}</p>
                        <p className="job-description">{truncateContent(job.content, 250)}</p>
                        <button 
                          className="apply-button"
                          onClick={() => navigate(`/careers/${job.id}`)}
                          disabled={isExpired}
                        >
                          {isExpired ? 'Position Closed' : 'Apply Now'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Create Job Popup */}
      {showCreatePopup && (
        <Portal>
        <div className="popup-overlay">
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="popup-header">
              <h2>Create New Position</h2>
              <button 
                className="close-button"
                onClick={() => {
                  setShowCreatePopup(false);
                  setFormData({
                    title: '',
                    work_model: '',
                    content: '',
                    compensation_min: '',
                    compensation_max: '',
                    location: '',
                    expiration: ''
                  });
                  setFieldErrors({});
                  setError('');
                }}
                type="button"
              >
                <X size={24} />
              </button>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <form onSubmit={handleCreateJob}>
              <div className="form-group">
                <label className={fieldErrors.title ? 'error-label' : ''}>Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Senior Software Engineer"
                />
                <div className={`char-count ${(formData.title?.length || 0) > 255 ? 'error' : (formData.title?.length || 0) > 229 ? 'warning' : ''}`}>
                  {formData.title?.length || 0} / 255
                </div>
              </div>

              <div className="form-group">
                <label className={fieldErrors.work_model ? 'error-label' : ''}>Work Model *</label>
                <select
                  name="work_model"
                  value={formData.work_model}
                  onChange={handleInputChange}
                >
                  <option value="">Select work model</option>
                  <option value="on-site">On-Site</option>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              <div className="form-group">
                <label className={fieldErrors.content ? 'error-label' : ''}>Job Description *</label>
                <textarea
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  placeholder="Detailed job description..."
                  rows="6"
                />
                <div className={`char-count ${(formData.content?.length || 0) > 10000 ? 'error' : (formData.content?.length || 0) > 9000 ? 'warning' : ''}`}>
                  {formData.content?.length || 0} / 10,000
                </div>
              </div>

              <div className="form-group">
                <label className={fieldErrors.location ? 'error-label' : ''}>Location *</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="1421 Solomon Rutter Rd • St. Leonard, MD 20685"
                />
                <div className={`char-count ${(formData.location?.length || 0) > 255 ? 'error' : (formData.location?.length || 0) > 229 ? 'warning' : ''}`}>
                  {formData.location?.length || 0} / 255
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className={fieldErrors.compensation_min ? 'error-label' : ''}>Min Compensation *</label>
                  <input
                    type="number"
                    name="compensation_min"
                    value={formData.compensation_min}
                    onChange={handleInputChange}
                    placeholder="50000"
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label className={fieldErrors.compensation_max ? 'error-label' : ''}>Max Compensation *</label>
                  <input
                    type="number"
                    name="compensation_max"
                    value={formData.compensation_max}
                    onChange={handleInputChange}
                    placeholder="100000"
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className={fieldErrors.expiration ? 'error-label' : ''}>Expiration Date *</label>
                <input
                  type="date"
                  name="expiration"
                  value={formData.expiration}
                  onChange={handleInputChange}
                  min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                />
              </div>

              <div className="popup-buttons">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setShowCreatePopup(false);
                    setFormData({
                      title: '',
                      work_model: '',
                      content: '',
                      compensation_min: '',
                      compensation_max: '',
                      location: '',
                      expiration: ''
                    });
                    setFieldErrors({});
                    setError('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="confirm-button">
                  Create Position
                </button>
              </div>
            </form>
          </div>
        </div>
        </Portal>
      )}

      {/* Edit Job Popup */}
      {showEditPopup && editingJob && (
        <Portal>
          <div className="popup-overlay">
            <div className="popup-content">
              <div className="popup-header">
                <h2>Edit Position</h2>
                <button 
                  className="close-button"
                  onClick={handleCancelEdit}
                  type="button"
                >
                  <X size={24} />
                </button>
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <form onSubmit={handleSaveEdit}>
                <div className="form-group">
                  <label className={fieldErrors.title ? 'error-label' : ''}>Job Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Senior Software Engineer"
                  />
                </div>

                <div className="form-group">
                  <label className={fieldErrors.work_model ? 'error-label' : ''}>Work Model *</label>
                  <select
                    name="work_model"
                    value={formData.work_model}
                    onChange={handleInputChange}
                  >
                    <option value="">Select work model</option>
                    <option value="on-site">On-Site</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className={fieldErrors.content ? 'error-label' : ''}>Job Description *</label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="Detailed job description..."
                    rows="6"
                  />
                </div>

                <div className="form-group">
                  <label className={fieldErrors.location ? 'error-label' : ''}>Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="1421 Solomon Rutter Rd • St. Leonard, MD 20685"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className={fieldErrors.compensation_min ? 'error-label' : ''}>Min Compensation *</label>
                    <input
                      type="number"
                      name="compensation_min"
                      value={formData.compensation_min}
                      onChange={handleInputChange}
                      placeholder="50000"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label className={fieldErrors.compensation_max ? 'error-label' : ''}>Max Compensation *</label>
                    <input
                      type="number"
                      name="compensation_max"
                      value={formData.compensation_max}
                      onChange={handleInputChange}
                      placeholder="100000"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className={fieldErrors.expiration ? 'error-label' : ''}>Expiration Date *</label>
                  <input
                    type="date"
                    name="expiration"
                    value={formData.expiration}
                    onChange={handleInputChange}
                    min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                  />
                </div>

                <div className="popup-buttons">
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={handleCancelEdit}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="confirm-button">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Portal>
      )}

      {/* Delete Confirmation Popup */}
      {showDeletePopup && jobToDelete && (
        <Portal>
          <div className="popup-overlay">
            <div className="popup-content delete-popup">
              <div className="popup-header">
                <h2>Delete Position</h2>
                <button 
                  className="close-button"
                  onClick={handleCancelDelete}
                  type="button"
                >
                  <X size={24} />
                </button>
              </div>
              <p>Are you sure you want to delete "{jobToDelete.title}"? This action cannot be undone.</p>
              <div className="popup-buttons">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="delete-button"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}

export default Careers;
